import { CampaignPlatform } from "../types/Platforms";

// Base platform icons (using lowercase keys)
export const platformIcons: Record<CampaignPlatform, string> = {
  x: "/icons/campaigns/x.svg",
  meta: "/icons/campaigns/meta.svg",
  linkedin: "/icons/campaigns/linkedin.svg",
  dv360: "/icons/campaigns/dv360.svg",
  instagram: "/icons/campaigns/instagram.svg",
  tiktok: "/icons/campaigns/tiktok.svg",
  natads: "/icons/campaigns/beapolo.svg",
  ctv: "/icons/campaigns/stream.svg",
};

// Additional platform icons not in the CampaignPlatform type
export const additionalPlatformIcons: Record<string, string> = {
  taboola: "/icons/campaigns/beapolo.svg",
  outbrain: "/icons/campaigns/beapolo.svg",
};

/**
 * Gets the platform icon path
 * @returns The path to the platform icon (always returns the BeApolo icon as per client request)
 */
export const getPlatformIconPath = (platform: string): string => {
  const normalizedPlatform = platform.toLocaleLowerCase();

  if (
    normalizedPlatform === "spotify_ads" ||
    normalizedPlatform === "spotify"
  ) {
    return "/icons/campaigns/audio.svg";
  }
  if (
    normalizedPlatform === "taboola" ||
    normalizedPlatform === "outbrain" ||
    normalizedPlatform === "natads"
  ) {
    return "/icons/campaigns/natads.svg";
  }
  if (normalizedPlatform === "hivestack") {
    return "/icons/campaigns/dooh.svg";
  }
  if (normalizedPlatform === "amazon_dsp" || normalizedPlatform === "amazon") {
    return "/icons/campaigns/video.svg";
  }
  if (normalizedPlatform === "ctv") {
    return "/icons/campaigns/stream.svg";
  }
  // All platforms use the same NATADS/BeApolo logo as requested by client
  return "/icons/campaigns/beapolo.svg";
};

/**
 * Gets the display name for a platform according to client requirements:
 * - Taboola and Outbrain should display as "NATADS"
 * - All other platforms should display as "BeApolo"
 *
 * @param platform - Original platform name
 * @returns The display name to show in the UI
 */
export const getPlatformDisplayName = (platform: string): string => {
  const normalizedPlatform = platform.toLowerCase();

  // Taboola and Outbrain should display as NATADS
  if (normalizedPlatform === "taboola" || normalizedPlatform === "outbrain") {
    return "NATADS";
  }
  if (
    normalizedPlatform === "spotify_ads" ||
    normalizedPlatform === "spotify"
  ) {
    return "Audio";
  }
  if (normalizedPlatform === "dv360" || normalizedPlatform === "beapolo") {
    return "BeApolo";
  }
  if (normalizedPlatform === "hivestack") {
    return "DOOH";
  }
  if (normalizedPlatform === "ctv") {
    return "CTV";
  }
  if (normalizedPlatform === "amazon_dsp" || normalizedPlatform === "amazon") {
    return "Amazon";
  }
  if (
    normalizedPlatform === "googleads" ||
    normalizedPlatform.startsWith("google_ads")
  ) {
    return "Google Ads";
  }
  if (normalizedPlatform === "meta") {
    return "Meta";
  }
  if (normalizedPlatform === "tiktok") {
    return "TikTok";
  }
  if (normalizedPlatform === "reddit") {
    return "Reddit";
  }
  return "Natads";
};

interface PlatformIconMeta {
  iconPath: string;
  displayName: string;
  normalizedKey: string;
}

const BRIEFING_PLATFORM_MAP: {
  matcher: (value: string) => boolean;
  key: string;
}[] = [
  { matcher: (value) => value.includes("spotify"), key: "spotify" },
  { matcher: (value) => value.includes("amazon"), key: "amazon_dsp" },
  {
    matcher: (value) => value.includes("hivestack") || value.includes("dooh"),
    key: "hivestack",
  },
  {
    matcher: (value) =>
      value.includes("ctv") ||
      value.includes("ott") ||
      value.includes("samsung"),
    key: "ctv",
  },
  {
    matcher: (value) => value.includes("netflix") || value.includes("disney"),
    key: "ctv",
  },
  {
    matcher: (value) =>
      value.includes("taboola") ||
      value.includes("outbrain") ||
      value.includes("natads"),
    key: "natads",
  },
  {
    matcher: (value) =>
      value.includes("dv360") ||
      value.includes("beapolo") ||
      value.includes("youtube"),
    key: "dv360",
  },
];

/**
 * Normalizes a backend briefing platform (e.g. BEAPOLO_DOOH, NATADS)
 * into the icon/display metadata used across the UI.
 */
export const resolveBriefingPlatformIcon = (
  platform?: string | null
): PlatformIconMeta => {
  if (!platform) {
    const fallbackIcon = getPlatformIconPath("natads");
    return {
      iconPath: fallbackIcon,
      displayName: getPlatformDisplayName("natads"),
      normalizedKey: "natads",
    };
  }

  const normalized = platform.toLowerCase();
  const matched =
    BRIEFING_PLATFORM_MAP.find((entry) => entry.matcher(normalized))?.key ??
    "natads";

  return {
    iconPath: getPlatformIconPath(matched),
    displayName: getPlatformDisplayName(matched),
    normalizedKey: matched,
  };
};
